import { useState, useRef, useMemo, useEffect } from 'react';
import { useFiltersWithNames } from './useFilters';
import { SectionVisibility } from '../types';
import { DEFAULT_EXPANDED_PANELS, DEFAULT_SECTION_VISIBILITY, MOCK_API_FILTERS } from '../constants/filterConstants';

export const useFilterDropdown = (
  selectedAction?: string | null,
  selectedCategory?: string | null,
  enableApiFilters: boolean = true,
  fallbackToMockData: boolean = true,
  locale: string = 'th'
) => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState(DEFAULT_EXPANDED_PANELS);
  const containerRef = useRef<HTMLDivElement>(null);

  // API integration
  const { 
    filters: apiFilters, 
    isLoading: isApiLoading, 
    error: apiError,
    isError: isApiError,
    filtersData,
    refetch
  } = useFiltersWithNames(
    enableApiFilters ? selectedAction : undefined,
    enableApiFilters ? selectedCategory : undefined,
    locale
  );

  // Force refetch เมื่อเปลี่ยน action, category หรือ locale
  useEffect(() => {
    if (enableApiFilters && selectedAction && selectedCategory) {
      refetch();
    }
  }, [selectedAction, selectedCategory, locale, enableApiFilters, refetch]);

  // Dynamic visibility based on API response
  const getDynamicVisibility = (): SectionVisibility => {
    // ถ้าไม่ใช้ API ให้แสดงทุก section
    if (!enableApiFilters) {
      return DEFAULT_SECTION_VISIBILITY;
    }

    // ถ้ากำลัง loading ให้ซ่อนทุก section
    if (isApiLoading) {
      return {
        price: false,
        bedroom: false,
        bathroom: false,
        area: false,
        quota: false,
        status: false,
        floor: false,
        view: false,
        direction: false,
        publishedBy: false,
        postedDate: false,
        distance: false,
      };
    }

    // ถ้า API error และไม่ใช้ fallback ให้ซ่อนทุก section
    if (isApiError && !fallbackToMockData) {
      return {
        price: false,
        bedroom: false,
        bathroom: false,
        area: false,
        quota: false,
        status: false,
        floor: false,
        view: false,
        direction: false,
        publishedBy: false,
        postedDate: false,
        distance: false,
      };
    }

    // กำหนด filters ที่จะใช้ - รวม API + fallback
    let availableFilters: string[] = [];
    
    if (isApiError && fallbackToMockData) {
      // ถ้า API error และใช้ fallback ให้ใช้ MOCK_API_FILTERS เท่านั้น
      availableFilters = MOCK_API_FILTERS;
    } else if (apiFilters && apiFilters.length > 0) {
      // ถ้ามี API data ให้รวม API filters + fallback filters
      const uniqueFilters = new Set([...apiFilters, ...MOCK_API_FILTERS]);
      availableFilters = Array.from(uniqueFilters);
    } else if (fallbackToMockData) {
      // ถ้าไม่มี API data และใช้ fallback ให้ใช้ MOCK_API_FILTERS
      availableFilters = MOCK_API_FILTERS;
    } else {
      // ถ้าไม่มี data และไม่ใช้ fallback ให้ใช้ array ว่าง
      availableFilters = [];
    }
    

    return {
      price: availableFilters.includes('price'),
      bedroom: availableFilters.includes('bedroom'),
      bathroom: availableFilters.includes('bathroom'),
      area: availableFilters.includes('area'),
      quota: availableFilters.includes('quota'),
      status: availableFilters.includes('status'),
      floor: availableFilters.includes('floor'),
      view: availableFilters.includes('view'),
      direction: availableFilters.includes('direction'),
      publishedBy: availableFilters.includes('publishedBy'),
      postedDate: availableFilters.includes('postedDate'),
      distance: availableFilters.includes('distance'),
    };
  };

  const sectionVisibility = useMemo(() => {
    const visibility = getDynamicVisibility();
    
    return visibility;
  }, [
    enableApiFilters,
    isApiLoading,
    isApiError,
    fallbackToMockData,
    apiFilters,
    selectedAction,
    selectedCategory,
    locale
  ]);

  // Reset expanded panels เมื่อเปลี่ยน action, category หรือ locale
  useEffect(() => {
    setExpandedPanels(DEFAULT_EXPANDED_PANELS);
  }, [selectedAction, selectedCategory, locale]);

  // Dropdown controls
  const toggleDropdown = () => {
    setIsOpen(prev => {
      const nextState = !prev;

      // ถ้าเป็นการเปิด dropdown (prev คือ false) ให้เรียก refetch เพื่อดึงข้อมูลกรองล่าสุดทุกครั้ง
      if (!prev && nextState) {
        try {
          // เงื่อนไข enableApiFilters และมีค่า action / category ครบถ้วนแล้วเท่านั้นถึงจะ refetch
          if (enableApiFilters && selectedAction && selectedCategory) {
            refetch();
          }
        } catch (e) {
          console.error('[useFilterDropdown] Failed to refetch filters on open', e);
        }
      }
      return nextState;
    });
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Panel controls
  const handlePanelChange = (panel: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setExpandedPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(panel)) {
        newSet.delete(panel);
      } else {
        newSet.add(panel);
      }
      return newSet;
    });
  };

  return {
    // State
    isOpen,
    expandedPanels,
    containerRef,
    sectionVisibility,
    
    // API state
    isApiLoading,
    apiError,
    isApiError,
    apiFilters,
    
    // Controls
    toggleDropdown,
    closeDropdown,
    handlePanelChange,
    
    // expose refetch in case parent component needs it in the future
    refetch,
  };
}; 